import cn from 'classnames';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateTimeLeft, isValidDate } from 'src/helpers/utils';
import './styles.scss';

type IProps = {
  classname?: string;
  targetDate: Date | string | null;
  onEndCb?: () => void;
  showDays?: boolean;
  showHours?: boolean;
  showMinutes?: boolean;
  view?: 'dots' | 'names';
  longNames?: boolean;
  size?: 'xxs' | 'xss' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
  bold?: boolean;
  uppercase?: boolean;
};

const CountdownTimer: FC<IProps> = ({
  classname,
  targetDate,
  onEndCb,
  showDays = true,
  showHours = true,
  showMinutes = true,
  view = 'dots',
  longNames = false,
  size = 'md',
  bold,
  uppercase,
}) => {
  const { t }: Translation = useTranslation();

  // States
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  const renderTimeBlock = (timeValue: string, view: 'names' | 'dots', timeName: string): ReactNode => {
    return (
      <>
        <div className="timer_block">
          <span className="timer_value">{timeValue}</span>
          <span className="timer_name">{view === 'names' ? `${timeName} ` : null}</span>
        </div>
        <span className="timer_dots"> : </span>
      </>
    );
  };

  // Effects
  useEffect((): any => {
    if (targetDate && isValidDate(targetDate)) {
      const timer = setInterval(() => {
        const timeLeft = calculateTimeLeft(targetDate);
        if (
          timeLeft.days === '00' &&
          timeLeft.hours === '00' &&
          timeLeft.minutes === '00' &&
          timeLeft.seconds === '00'
        ) {
          clearInterval(timer);
          !!onEndCb && onEndCb();
        }
        setTimeLeft(timeLeft);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [targetDate]);

  return (
    <div
      className={cn(
        'timer',
        {
          [`timer_view__${view}`]: view,
          [`timer_size__${size}`]: size,
          ['timer_text__bold']: bold,
          ['timer_text__uppercase']: uppercase,
        },
        classname
      )}
    >
      {showDays && renderTimeBlock(timeLeft.days, view, longNames ? t('days') : 'd')}
      {view === 'dots' && showDays && <span> : </span>}
      {showHours && renderTimeBlock(timeLeft.hours, view, longNames ? t('hours') : 'h')}
      {view === 'dots' && showHours && <span> : </span>}
      {showMinutes && renderTimeBlock(timeLeft.minutes, view, longNames ? t('minutes') : 'm')}
      {view === 'dots' && showMinutes && <span> : </span>}
      {renderTimeBlock(timeLeft.seconds, view, longNames ? t('secondsLong') : 's')}
    </div>
  );
};

export default CountdownTimer;
